import { CircleCheck, Clock, Plus, Search } from 'lucide-react';
import { useId, useState } from 'react';
import { Link } from 'react-router';
import { useNetworkKinds } from '../api/queries';
import type { EdgeKind, EntryPointSettings } from '../api/types';
import { ActionButton, Dialog, ErrorMessage, Loading } from '../components/ui';
import { strings } from '../strings';
import { PlanNetworkButton } from './deployment/NetworkProblem';
import { EntryPointCard } from './entryPoints/EntryPointCard';
import { RemoveEntryPointDialog, RotateOriginSecretDialog } from './entryPoints/EntryPointDialogs';
import { EntryPointForm } from './entryPoints/EntryPointForm';
import { edgeOf } from './entryPoints/helpers';
import { kindMatches, kindReasonLabel, rovingKeyDown } from './lists/helpers';
import { useManageNetworks } from './shared';
import type { NetworkTabProps } from './tabProps';

const e = strings.networks.lists.entryPoints;
const ep = strings.networks.entryPoints;

/** "Global · Standard USD 35/month, Premium USD 330/month". */
function scopeAndCosts(kind: EdgeKind): string {
  const costs = kind.tiers
    .filter((t) => t.cost)
    .map((t) => e.tierCost(t.label, t.cost!.currency, t.cost!.monthly))
    .join(', ');
  return [kind.scope ? e.scope[kind.scope] : null, costs || null].filter(Boolean).join(' · ');
}

/**
 * Add entry point's kind picker (spec 11 6.15.10, mock n05b): every kind of the registry in one radiogroup with roving
 * focus, the available ones first as tiles, then the others as compact rows with their reason (`aria-disabled`,
 * focusable, described by the reason). Phase 1 has no available kind: Continue stays `aria-disabled`.
 */
export function KindPicker({
  networkId,
  network,
  onClose,
  onContinue,
}: {
  networkId: string;
  network: string;
  onClose: () => void;
  /** Opens the entry point form for the chosen kind (phase 3). */
  onContinue?: (kind: EdgeKind) => void;
}) {
  const kinds = useNetworkKinds(networkId);
  const admin = useManageNetworks();
  const ids = useId();
  const [query, setQuery] = useState('');
  const [chosen, setChosen] = useState<string | null>(null);
  const edges = kinds.data?.edges ?? [];
  const matching = edges.filter((k) => kindMatches(k, query));
  const available = matching.filter((k) => k.status === 'available');
  const unavailable = matching.filter((k) => k.status !== 'available');
  const ordered = [...available, ...unavailable];
  const anyAvailable = edges.some((k) => k.status === 'available');
  const radioId = (i: number) => `${ids}-kind-${i}`;
  const allIds = ordered.map((_, i) => radioId(i));
  const checked = ordered.findIndex((k) => k.kind === chosen);
  const stop = checked >= 0 ? checked : 0;
  const choose = (i: number) => {
    const kind = ordered[i];
    if (kind?.status === 'available') setChosen(kind.kind);
  };
  const chosenKind = edges.find((k) => k.kind === chosen && k.status === 'available') ?? null;
  const continueReason = chosenKind ? null : anyAvailable ? e.chooseKind : e.noneAvailable;
  const continueReasonId = `${ids}-continue-reason`;
  // A `needs-provider` reason's action ("Add AWS to Production") for network admins; links can't sit inside the
  // radiogroup (only radios may), so they follow it.
  const actions = admin
    ? unavailable.flatMap((kind) =>
        kind.status === 'needs-provider' && kind.reason?.action ? [{ kind, action: kind.reason.action }] : [],
      )
    : [];

  const row = (kind: EdgeKind, i: number) => {
    const isAvailable = kind.status === 'available';
    const reasonId = `${ids}-reason-${i}`;
    const badge = kindReasonLabel(kind.status, kind.provider, network);
    return (
      <div
        key={kind.kind}
        id={radioId(i)}
        role="radio"
        aria-checked={kind.kind === chosen}
        aria-disabled={!isAvailable || undefined}
        aria-describedby={isAvailable ? undefined : reasonId}
        tabIndex={i === stop ? 0 : -1}
        className={isAvailable ? 'net-edge-tile' : 'net-edge-row'}
        data-testid={`kind-${kind.kind}`}
        onClick={() => choose(i)}
        onKeyDown={(event) => rovingKeyDown(event, allIds, i, choose)}
      >
        <span aria-hidden className={`net-edge-abbr ${isAvailable ? '' : 'is-muted'}`}>
          {kind.abbr}
        </span>
        {isAvailable ? (
          <span className="min-w-0 flex-1">
            <span className="block font-semibold">{kind.label}</span>
            {kind.addOns.length > 0 && (
              <span className="mt-1 flex flex-wrap gap-1">
                {kind.addOns.map((addOn) => (
                  <span key={addOn.id} className="net-addon">
                    {addOn.label}
                  </span>
                ))}
              </span>
            )}
            <span className="mt-1 block text-[12.5px] text-fg-muted">{scopeAndCosts(kind)}</span>
          </span>
        ) : (
          <>
            <span className="net-edge-label font-semibold">{kind.label}</span>
            <span className="net-edge-summary text-[12.5px]">{kind.addOnSummary}</span>
            <span className="badge tone-neutral shrink-0">
              <Clock aria-hidden className="size-3.5" />
              {badge}
            </span>
            <span id={reasonId} className="sr-only">
              {kind.reason?.text ?? badge}
            </span>
          </>
        )}
      </div>
    );
  };

  return (
    <Dialog
      title={e.pickerTitle}
      onClose={onClose}
      wide
      className="dialog-sheet"
      footer={
        <>
          <button type="button" className="btn" onClick={onClose}>
            {strings.common.cancel}
          </button>
          <button
            type="button"
            className="btn btn-primary"
            aria-disabled={continueReason ? true : undefined}
            aria-describedby={continueReason ? continueReasonId : undefined}
            title={continueReason ?? undefined}
            data-testid="kind-continue"
            onClick={(event) => {
              if (continueReason || !chosenKind) {
                event.preventDefault();
                return;
              }
              onContinue?.(chosenKind);
            }}
          >
            {chosenKind ? e.continueWith(chosenKind.label) : e.continue}
          </button>
          {continueReason && (
            <span id={continueReasonId} className="sr-only">
              {continueReason}
            </span>
          )}
        </>
      }
    >
      <div className="relative mb-3">
        <Search
          aria-hidden
          className="pointer-events-none absolute top-1/2 left-2.5 size-4 -translate-y-1/2 text-fg-muted"
        />
        <label htmlFor={`${ids}-search`} className="sr-only">
          {e.search}
        </label>
        <input
          id={`${ids}-search`}
          type="search"
          className="input w-full pl-8"
          placeholder={e.search}
          value={query}
          onChange={(event) => setQuery(event.target.value)}
        />
      </div>
      {kinds.error ? (
        <ErrorMessage error={kinds.error} onRetry={() => void kinds.refetch()} />
      ) : !kinds.data ? (
        <Loading />
      ) : (
        <>
          {!anyAvailable && (
            <p className="callout callout-info mb-3" data-testid="no-kind-available">
              {e.noneAvailable}
            </p>
          )}
          {ordered.length === 0 ? (
            <p className="help">{e.noMatch}</p>
          ) : (
            <div role="radiogroup" aria-label={e.kinds} className="net-edge-group">
              {available.length > 0 && (
                <p className="net-edge-heading" aria-hidden>
                  {e.available(network, available.length)}
                </p>
              )}
              {available.length > 0 && (
                <div className="net-edge-tiles">{available.map((k, i) => row(k, i))}</div>
              )}
              {unavailable.length > 0 && (
                <p className="net-edge-heading" aria-hidden>
                  {e.notAvailable(unavailable.length)}
                </p>
              )}
              {unavailable.map((k, i) => row(k, available.length + i))}
            </div>
          )}
          {actions.length > 0 && (
            <ul className="net-edge-actions">
              {actions.map(({ kind, action }) => (
                <li key={kind.kind}>
                  <span className="text-fg-muted">{kind.label}:</span>{' '}
                  <Link className="link" to={action.href} onClick={onClose}>
                    {action.label}
                  </Link>
                </li>
              ))}
            </ul>
          )}
          {!onContinue && <p className="help mt-3">{e.phaseThree}</p>}
        </>
      )}
    </Dialog>
  );
}

/** The Entry points tab (spec 11 6.15.10, mocks n05, n05b): the cards, Add entry point's kind picker and the form. */
export function EntryPointsTab({ network }: NetworkTabProps) {
  const n = network.data;
  const kinds = useNetworkKinds(n.id);
  const manage = useManageNetworks();
  const [picking, setPicking] = useState(false);
  const [form, setForm] = useState<{ kind: EdgeKind; editing: EntryPointSettings | null } | null>(null);
  const [rotating, setRotating] = useState<EntryPointSettings | null>(null);
  const [removing, setRemoving] = useState<EntryPointSettings | null>(null);
  const [expanded, setExpanded] = useState<Set<string>>(
    () => new Set(n.settings.entryPoints.slice(0, 1).map((x) => x.id)),
  );
  const [notice, setNotice] = useState<string | null>(null);
  const eps = n.settings.entryPoints;
  const routes = eps.reduce((sum, x) => sum + (x.routes?.length ?? 0), 0);
  const toggle = (id: string) =>
    setExpanded((set) => {
      const next = new Set(set);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  return (
    <section aria-labelledby="entry-points-heading" className="space-y-3" data-testid="entry-points-tab">
      <div className="flex flex-wrap items-center gap-2">
        <h2 id="entry-points-heading" className="text-[15px] font-semibold">
          {e.title}
        </h2>
        {eps.length > 0 && (
          <span className="text-[12.5px] text-fg-muted">{ep.summary(eps.length, routes)}</span>
        )}
        <span className="ml-auto">
          <ActionButton
            permission="manage-networks"
            variant="primary"
            icon={<Plus aria-hidden className="size-4" />}
            onClick={() => setPicking(true)}
          >
            {e.add}
          </ActionButton>
        </span>
      </div>
      {notice && (
        <div className="callout callout-success" role="status" data-testid="ep-saved">
          <CircleCheck aria-hidden className="size-4" />
          <span className="flex flex-wrap items-center gap-2">
            {notice}
            {manage && <PlanNetworkButton networkId={n.id} />}
          </span>
        </div>
      )}
      {eps.length === 0 && <p className="help">{e.none}</p>}
      <div className="space-y-3">
        {eps.map((x) => (
          <EntryPointCard
            key={x.id}
            network={n}
            ep={x}
            kind={edgeOf(kinds.data, x.kind)}
            expanded={expanded.has(x.id)}
            onToggle={() => toggle(x.id)}
            manage={manage}
            onEdit={() => {
              const kind = edgeOf(kinds.data, x.kind);
              if (kind) setForm({ kind, editing: x });
            }}
            onRotate={() => setRotating(x)}
            onRemove={() => setRemoving(x)}
          />
        ))}
      </div>
      {picking && (
        <KindPicker
          networkId={n.id}
          network={n.name}
          onClose={() => setPicking(false)}
          onContinue={(kind) => {
            setPicking(false);
            setForm({ kind, editing: null });
          }}
        />
      )}
      {form && kinds.data && (
        <EntryPointForm
          network={network}
          kind={form.kind}
          editing={form.editing}
          onClose={() => setForm(null)}
          onSaved={(message, id) => {
            setForm(null);
            setNotice(message);
            setExpanded((set) => new Set(set).add(id));
          }}
        />
      )}
      {rotating && <RotateOriginSecretDialog network={n} ep={rotating} onClose={() => setRotating(null)} />}
      {removing && (
        <RemoveEntryPointDialog
          network={network}
          ep={removing}
          onClose={() => setRemoving(null)}
          onRemoved={(message) => {
            setRemoving(null);
            setNotice(message);
          }}
        />
      )}
    </section>
  );
}
